import React from 'react';
import { AlertTriangle, Gavel, Skull } from 'lucide-react';
import useGameStore from '../../store/gameStore';

/**
 * Top sticky banner that surfaces the 3 escalating bankruptcy alerts:
 *   - amber: vigilance, 30+ days runway
 *   - rose : danger, ≤14 days runway, AGE proposable
 *   - red  : liquidation imminent, ≤7 days, AGE almost-must
 *
 * Click on the banner opens the AGE modal (if available & not used).
 * Hidden when alertStage is null OR gameOverState exists (post-liquidation).
 */
export default function BankruptcyAlertBanner() {
  const stage = useGameStore(s => s.bankruptcyAlertStage);
  const gameOver = useGameStore(s => s.gameOverState);
  const insolvencyStreak = useGameStore(s => s.insolvencyStreak || 0);
  const emergencyUsed = useGameStore(s => s.emergencyShareholderUsed);
  const pendingMeeting = useGameStore(s => s.pendingShareholderMeeting);

  if (!stage || gameOver) return null;

  const daysLeft = Math.max(0, 30 - insolvencyStreak);

  const tone = {
    amber: {
      bg: 'from-amber-500/30 to-amber-600/20',
      border: 'border-amber-400/60',
      text: 'text-amber-100',
      icon: <AlertTriangle className="w-5 h-5 text-amber-300" />,
      label: 'VIGILANCE',
      headline: 'Vos finances dérapent',
    },
    rose: {
      bg: 'from-rose-500/35 to-rose-600/25',
      border: 'border-rose-400/60',
      text: 'text-rose-100',
      icon: <Gavel className="w-5 h-5 text-rose-300" />,
      label: 'DANGER',
      headline: 'Cessation de paiements imminente',
    },
    red: {
      bg: 'from-red-600/45 to-red-700/35 animate-pulse',
      border: 'border-red-400',
      text: 'text-red-50',
      icon: <Skull className="w-5 h-5 text-red-200" />,
      label: 'LIQUIDATION IMMINENTE',
      headline: 'Dernière chance — agissez maintenant',
    },
  }[stage];

  const handleOpenAGE = () => {
    if (emergencyUsed || pendingMeeting) return;
    // Use the helper that fires the streamlined "open AGE" action — we set the
    // pending meeting directly from current state so the modal pops.
    const state = useGameStore.getState();
    const treasury = state.treasury || 0;
    // Recompute the recap amount on the fly from the balance sheet.
    // Use a soft-fallback to the streak heuristic if the deficit can't be
    // determined here (shouldn't happen, but safe).
    const deficit = Math.max(0, -treasury) + 5000;
    useGameStore.setState({
      pendingShareholderMeeting: {
        recapAmount: Math.round(deficit * 0.5),
        deficit,
        daysUntilLiquidation: daysLeft,
      },
    });
  };

  return (
    <div
      className={`sticky top-0 z-40 border-b-2 ${tone.border} bg-gradient-to-r ${tone.bg} backdrop-blur-sm`}
      data-testid={`bankruptcy-banner-${stage}`}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          {tone.icon}
          <span className={`text-[10px] font-bold tracking-wider ${tone.text}`}>
            {tone.label}
          </span>
        </div>
        <div className={`flex-1 text-sm ${tone.text}`}>
          <span className="font-bold">{tone.headline}</span>
          <span className="opacity-80"> · Plus que </span>
          <span className="font-mono font-bold" data-testid="bankruptcy-days-left">
            {daysLeft} jour{daysLeft > 1 ? 's' : ''}
          </span>
          <span className="opacity-80"> avant liquidation judiciaire.</span>
        </div>
        {(stage === 'rose' || stage === 'red') && !emergencyUsed && !pendingMeeting && (
          <button
            type="button"
            onClick={handleOpenAGE}
            className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-bold whitespace-nowrap transition-colors"
            data-testid="bankruptcy-open-age"
          >
            Convoquer une AGE
          </button>
        )}
        {emergencyUsed && (
          <span className={`text-[10px] uppercase tracking-wider ${tone.text} opacity-70`} data-testid="bankruptcy-age-used">
            AGE utilisée
          </span>
        )}
      </div>
    </div>
  );
}
